import { ownKeys, ownEntries } from '#library/primitive.library.js';
import { isObject, isArray, isFunction, isDefined, isNullish, isMap, isSet, isDate, isRegExp, isSafeKey, isPrimitive } from '#library/assertion.library.js';
import { getType, cast } from '#library/type.library.js';
const RE_UNQUOTE_PROP = /"([^"]+)":/g;
const RE_COMMA = /,/g;
/**
 * Serializes an object to JSON and removes quotes around property names.
 * Useful for generating loosely formatted string representations of objects.
 *
 * @param obj - The object to un-quote
 * @returns A stringified JSON representation without quotes around keys
 * @example
 * ```ts
 * unQuoteObj({ a: 1 }); // '{a: 1}'
 * ```
 */
export const unQuoteObj = (obj) => {
    return JSON.stringify(obj)
        ?.replace(RE_UNQUOTE_PROP, '$1: ')
        ?.replace(RE_COMMA, ', ');
};
/**
 * Safely collects own and prototype getter / enumerable property keys across the inheritance chain.
 * Excludes unsafe prototype pollution keys and Object.prototype built-ins.
 */
const getObjectKeys = (obj) => {
    const keys = new Set();
    let curr = obj;
    let depth = 0;
    while (curr && curr !== Object.prototype && depth++ < 10) {
        const descs = Object.getOwnPropertyDescriptors(curr);
        for (const [key, desc] of Object.entries(descs)) {
            if (!isSafeKey(key))
                continue;
            if (desc.enumerable || isFunction(desc.get))
                keys.add(key);
        }
        curr = Object.getPrototypeOf(curr);
    }
    return Array.from(keys);
};
/**
 * Recursively converts an object or class/host instance with prototype getters into a plain object.
 * Returns the original value if it is not an object or is nullish, or specialized types (Date, RegExp, Map, Set).
 *
 * @param obj - The object to convert
 * @returns A new plain object with copied/evaluated properties
 * @example
 * ```ts
 * const copy = asObject({ a: 1 });
 * ```
 */
export const asObject = (obj) => {
    if (isPrimitive(obj) || isFunction(obj))
        return obj;
    if (isArray(obj))
        return cast(obj.map(item => asObject(item)));
    if (isDate(obj) || isRegExp(obj) || isMap(obj) || isSet(obj))
        return obj;
    try {
        if (isFunction(obj.toJSON))
            return asObject(obj.toJSON());
    }
    catch {
        // fallback to property extraction if toJSON throws
    }
    const temp = {};
    for (const key of getObjectKeys(obj)) {
        try {
            temp[key] = asObject(obj[key]);
        }
        catch {
            // ignore unreadable or throwing getters
        }
    }
    return temp;
};
/**
 * Performs a deep comparison between two values to determine if they are equivalent.
 * Supports primitives, arrays, maps, sets, and plain objects.
 *
 * @param a - The first value to compare
 * @param b - The second value to compare
 * @returns True if the values are deeply equal
 * @example
 * ```ts
 * isEqual({ a: 1 }, { a: 1 }); // true
 * ```
 */
export const isEqual = (a, b) => {
    if (a === b)
        return true;
    if (isNullish(a) || isNullish(b))
        return a === b;
    if (getType(a) !== getType(b))
        return false;
    if (isArray(a) && isArray(b)) {
        const left = a, right = b;
        return left.length === right.length && left.every((v, i) => isEqual(v, right[i]));
    }
    if (isMap(a) && isMap(b)) {
        const left = a, right = b;
        return left.size === right.size &&
            Array.from(left.keys()).every(k => right.has(k) && isEqual(left.get(k), right.get(k)));
    }
    if (isSet(a) && isSet(b)) {
        const left = a, right = b;
        if (left.size !== right.size)
            return false;
        const rightArr = Array.from(right);
        const matched = new Set();
        for (const lVal of left) {
            const foundIdx = rightArr.findIndex((rVal, idx) => !matched.has(idx) && isEqual(lVal, rVal));
            if (foundIdx === -1)
                return false;
            matched.add(foundIdx);
        }
        return true;
    }
    if (isObject(a) && isObject(b)) {
        const left = a, right = b;
        const keys = new Set();
        const keys1 = (isFunction(left.keys) && Object.getPrototypeOf(left) !== Object.prototype) ? Array.from(left.keys()) : ownKeys(left);
        const keys2 = (isFunction(right.keys) && Object.getPrototypeOf(right) !== Object.prototype) ? Array.from(right.keys()) : ownKeys(right);
        keys1.forEach(k => keys.add(k));
        keys2.forEach(k => keys.add(k));
        if (keys.size !== keys1.length || keys.size !== keys2.length)
            return false;
        return [...keys].every(k => isEqual(left[k], right[k]));
    }
    return false;
};
/**
 * Finds all method names on an object.
 *
 * @param obj - The object to inspect
 * @param all - Whether to traverse the prototype chain (default: false)
 * @returns An array of property keys corresponding to functions
 * @example
 * ```ts
 * const methods = getMethods(myClassInstance);
 * ```
 */
export const getMethods = (obj, all = false) => {
    const properties = new Set();
    let currentObj = obj;
    do {
        Object
            .getOwnPropertyNames(currentObj)
            .map(key => properties.add(key));
    } while (all && (currentObj = Object.getPrototypeOf(currentObj)));
    return [...properties.keys()]
        .filter(key => isFunction(obj[key]));
};
/**
 * Extracts a new object containing only the properties with defined (non-undefined) values.
 *
 * @param obj - The object to extract from
 * @returns A new object without undefined values
 * @example
 * ```ts
 * const clean = ifDefined({ a: 1, b: undefined }); // { a: 1 }
 * ```
 */
export function ifDefined(obj) {
    return ownEntries(obj)
        .reduce((acc, [key, val]) => {
        if (isSafeKey(key) && isDefined(val))
            acc[key] = val;
        return acc;
    }, {});
}
export function pick(obj, ...keys) {
    const flatKeys = keys.flat();
    const result = {};
    for (const key of flatKeys) {
        if (key in obj) {
            Object.defineProperty(result, key, {
                value: obj[key],
                writable: true,
                enumerable: true,
                configurable: true
            });
        }
    }
    return result;
}
/**
 * Extracts a specified named key from an array of objects.
 *
 * @param objs - The array of objects
 * @param key - The property key to extract
 * @returns An array of the extracted property values
 * @example
 * ```ts
 * const ids = pluck([{ id: 1 }, { id: 2 }], 'id'); // [1, 2]
 * ```
 */
export const pluck = (objs, key) => objs.map(obj => obj[key]);
/**
 * Extends a target object with the properties of one or more source objects.
 * Uses `Object.assign` internally.
 *
 * @param obj - The target object
 * @param objs - The source objects
 * @returns The extended target object
 * @example
 * ```ts
 * const ext = extend({ a: 1 }, { b: 2 }); // { a: 1, b: 2 }
 * ```
 */
export const extend = (obj, ...objs) => Object.assign(obj, ...objs);
/**
 * Recursively deeply merges multiple objects into a single new object.
 * Does not mutate the source objects.
 *
 * @param objects - The objects to merge
 * @returns A new deeply merged object
 * @example
 * ```ts
 * const merged = deepMerge({ a: { x: 1 } }, { a: { y: 2 } }); // { a: { x: 1, y: 2 } }
 * ```
 */
export const deepMerge = (...objects) => {
    return objects.reduce((prev, obj) => {
        if (!isObject(obj))
            return prev;
        Object.entries(obj).forEach(([key, value]) => {
            if (!isSafeKey(key))
                return;
            const pVal = prev[key];
            if (isObject(pVal) && isObject(value)) {
                prev[key] = deepMerge(pVal, value);
            }
            else if (isObject(value)) {
                prev[key] = deepMerge({}, value);
            }
            else {
                prev[key] = value;
            }
        });
        return prev;
    }, {});
};
/**
 * Returns the count of enumerable own properties on an object.
 *
 * @param obj - The object to count properties of
 * @returns The number of properties
 * @example
 * ```ts
 * const count = countProperties({ a: 1, b: 2 }); // 2
 * ```
 */
export const countProperties = (obj = {}) => ownKeys(obj).length;
export function looseIndex(arg) {
    if (isDefined(arg))
        return isFunction(arg) ? arg() : arg;
    return (obj) => isFunction(obj) ? obj() : obj;
}
/** loose object with symbols values */
looseIndex.stringSymbol = looseIndex();
/** loose object with symbol keys and RegExp values */
looseIndex.symbolRegExp = looseIndex();
/** loose object with symbol keys and string values */
looseIndex.symbolString = looseIndex();
/** loose object with string keys and string values */
looseIndex.stringString = looseIndex();
